/**
 * The page-view queue (docs/activity-and-audit.md, "Capture"). The router
 * records every resolved page; the queue sends them in batches every few
 * seconds, and at once when the page is hidden (`visibilitychange`), so
 * views are not lost when a tab closes.
 *
 * A view carries the route's template ID, its parameters (IDs only: any
 * value that is not a UUID is dropped), the previous route and how long the
 * previous page was open. Never a query string, a title or page content.
 *
 * The app sends them with `apiTransport` (`transport.ts`) to
 * `POST /api/v1/activity/page-views`; the default here, `noTransport`,
 * sends nothing (tests).
 */

/** One page view, as the API will receive it. */
export interface PageView {
  /** The route's template ID, for example `/school/candidates/$candidateId`. */
  routeId: string;
  /** Route parameters whose values are IDs. */
  params: Record<string, string>;
  /** The previous page's route ID, or `null` for the first view in this tab. */
  referrerRouteId: string | null;
  /** How long the previous page was open, in milliseconds (`null` for the first view). */
  durationOnPreviousMs: number | null;
  /** When the view happened (ISO 8601). */
  at: string;
}

/**
 * Sends one batch. Resolves `true` when the batch was accepted; `false` to
 * keep it queued for the next flush. `beacon` is set when the page is being
 * hidden, so the transport should use `navigator.sendBeacon` or
 * `fetch(…, { keepalive: true })`.
 */
export type PageViewTransport = (
  batch: readonly PageView[],
  options: { beacon: boolean },
) => Promise<boolean>;

/** A transport that accepts every batch and sends nothing (tests, and the gallery). */
export const noTransport: PageViewTransport = () => Promise.resolve(true);

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Keeps only parameters whose values are UUIDs. */
export function idParams(params: Record<string, unknown>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(params).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string' && UUID.test(entry[1]),
    ),
  );
}

/** Options for `PageViewQueue`. */
export interface PageViewQueueOptions {
  transport?: PageViewTransport;
  /** How often to send, in milliseconds (default 5000). */
  intervalMs?: number;
  /** Most views kept while sending fails; the oldest are dropped first (default 200). */
  maxQueued?: number;
  /** Most views per batch (default 50). */
  maxBatch?: number;
  /** The clock (tests). */
  now?: () => number;
}

/** Queues page views and sends them in batches. */
export class PageViewQueue {
  private readonly transport: PageViewTransport;
  private readonly intervalMs: number;
  private readonly maxQueued: number;
  private readonly maxBatch: number;
  private readonly now: () => number;
  private queue: PageView[] = [];
  private previous: { routeId: string; paramsKey: string; since: number } | undefined;
  private timer: ReturnType<typeof setInterval> | undefined;
  private sending = false;

  constructor(options: PageViewQueueOptions = {}) {
    this.transport = options.transport ?? noTransport;
    this.intervalMs = options.intervalMs ?? 5000;
    this.maxQueued = options.maxQueued ?? 200;
    this.maxBatch = options.maxBatch ?? 50;
    this.now = options.now ?? Date.now;
  }

  /** The views waiting to be sent (tests and debugging). */
  get pending(): readonly PageView[] {
    return this.queue;
  }

  /**
   * Records a view. A repeat of the current route with the same parameters
   * (a re-render or a search change) is not a new view.
   *
   * @param routeId - The resolved route's template ID.
   * @param params - Its parameters; non-ID values are dropped.
   */
  record(routeId: string, params: Record<string, unknown> = {}): void {
    const now = this.now();
    const ids = idParams(params);
    const paramsKey = JSON.stringify(ids);
    if (this.previous?.routeId === routeId && this.previous.paramsKey === paramsKey) return;
    this.queue.push({
      routeId,
      params: ids,
      referrerRouteId: this.previous?.routeId ?? null,
      durationOnPreviousMs: this.previous ? now - this.previous.since : null,
      at: new Date(now).toISOString(),
    });
    if (this.queue.length > this.maxQueued)
      this.queue.splice(0, this.queue.length - this.maxQueued);
    this.previous = { routeId, paramsKey, since: now };
  }

  /**
   * Sends what is queued, one batch at a time. Failed batches stay queued.
   *
   * @param beacon - The page is being hidden; send without waiting for a reply.
   */
  async flush(beacon = false): Promise<void> {
    if (this.sending || this.queue.length === 0) return;
    this.sending = true;
    try {
      while (this.queue.length > 0) {
        const batch = this.queue.slice(0, this.maxBatch);
        let accepted = false;
        try {
          accepted = await this.transport(batch, { beacon });
        } catch {
          accepted = false;
        }
        if (!accepted) return;
        this.queue.splice(0, batch.length);
      }
    } finally {
      this.sending = false;
    }
  }

  /**
   * Starts the timer and the `visibilitychange` flush.
   *
   * @returns Stops both.
   */
  start(): () => void {
    const onHidden = () => {
      if (document.visibilityState === 'hidden') void this.flush(true);
    };
    this.timer = setInterval(() => {
      void this.flush();
    }, this.intervalMs);
    document.addEventListener('visibilitychange', onHidden);
    return () => {
      if (this.timer !== undefined) clearInterval(this.timer);
      this.timer = undefined;
      document.removeEventListener('visibilitychange', onHidden);
    };
  }
}
